import { Box, Collapse, Divider, FormControl, FormLabel, Grid, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AddCircleOutline, ChatBubble, Edit, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Link } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/redux";
import { colors, alphaVar } from "../../ui/colors";
import { transformEventBase } from "./helper/transforEventScheduler";
import { EMonetaryAmountBy } from "../../invoice/create/interfaces/invoice.interfaces";
import {
  setAlias,
  setClient,
  setCreditedDepositAmount,
  setDescription,
  setNotes,
  setFrequencyType,
  setScopeOfWorkCostOverride,
  setServiceNameOverride,
} from "../../../redux/features/jobCreateSlice";
import { EServiceType, getRecordEmail, getRecordPhone, menuPropsModal } from "../../../utils";
import { useQuoteDepositAvailable } from "./hooks/useQuoteDepositAvailable";
import { useModals } from "../../../context/ModalContext";
import { InvoiceDetailsView } from "../../invoice/view/components/DetailsView";
import { RELATED_DEPOSIT_INVOICE_MODAL_KEY } from "../../invoice/RelatedDepositInvoiceModalHost";
import { TaskNoteBubble } from "@components/shared/components/features/scope-of-work/components/areas/Tasks/TaskNoteBubble";
import { ScopeOfWorkStep } from "@components/shared/components/features/scope-of-work/context/scope-of-work.types";
import { ServiceNameOverrideRow } from "./ServiceNameOverrideRow";
import { ServiceAliasField } from "@components/common/ServiceAliasField";
import { EFrequencyType, JOB_FREQUENCY_OPTIONS, getJobFrequencyLabel } from "@components/scope-of-work/helpers";
import { JobNotesCard } from "../../jobs/jobs-details/components/JobNotesCard";

const monetaryAmountOptions = [
  { value: EMonetaryAmountBy.MONTHLY, label: "Monthly" },
  { value: EMonetaryAmountBy.YEARLY, label: "Yearly" },
  { value: EMonetaryAmountBy.UNIQUE, label: "One Time" },
  { value: EMonetaryAmountBy.PER_ITEM, label: "Per Item" },
  { value: EMonetaryAmountBy.PER_SQUARE_FOOT, label: "Per Square Foot" },
  { value: EMonetaryAmountBy.PER_CAR_SPACE, label: "Per Car Space" },
  { value: EMonetaryAmountBy.PER_FLOOR, label: "Per Floor" },
];

export const JobCreateSummary = ({ goToStepByKey, isUpdateJob = false, clientLocked = false }) => {
  const { quote, selectedService, eventScheduler, scopeOfWork, quoteMode } = useAppSelector((state) => state.jobCreateStepper);
  const { openModal } = useModals();
  const dispatch = useAppDispatch();
  const depositQuery = useQuoteDepositAvailable(quote?.id);
  const depositAvailable = Math.max(0, Number(depositQuery.data?.available ?? 0) || 0);
  const depositContractAmount = Number(depositQuery.data?.depositAmount ?? quote?.depositAmount ?? 0) || 0;
  const depositInvoiceUid = depositQuery.data?.depositInvoiceUid ?? quote?.depositInvoiceUid ?? null;

  const quoteType = quote?.type || quoteMode;
  const isFromQuote = Boolean(quote?.id);
  const isAdvanced = quoteType === "advanced";
  const quoteServices = Array.isArray(quote?.services) ? quote.services : [];
  const selectedServices = Array.isArray(selectedService) ? selectedService : selectedService ? [selectedService] : [];
  const serviceLines = selectedServices.length > 0 ? selectedServices : quoteServices;
  const service = serviceLines[0] || null;

  const quoteService = quoteServices.find((item) => item?.serviceUid === service?.serviceUid) || quoteServices[0] || null;

  const getName = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;

    return value.name || value.label || value.title || value.type || "";
  };

  const getServiceLineName = (item, index = 0) => {
    return item?.serviceNameOverride?.trim() || getName(item?.service) || `Service ${index + 1}`;
  };

  const scopeOfWorkFromState = Array.isArray(scopeOfWork) ? scopeOfWork[0] : scopeOfWork;
  const scopeOfWorkFromQuote = service?.scopeOfWork || service?.service?.scopeOfWork || quoteServices[0]?.scopeOfWork || quote?.scopeOfWork || null;
  const sectorWorkflow = isAdvanced ? scopeOfWorkFromState || scopeOfWorkFromQuote || {} : service;
  const originalServiceName = getName(service?.service) || "-";
  const basicServiceNameOverride = service?.serviceNameOverride ?? "";

  const advancedServiceNameOverride =
    sectorWorkflow?.serviceNameOverride ?? service?.scopeOfWork?.serviceNameOverride ?? quote?.scopeOfWork?.serviceNameOverride ?? "";

  const serviceNameOverride = isAdvanced ? advancedServiceNameOverride : basicServiceNameOverride;
  const fullState = useAppSelector((state) => state.jobCreateStepper);
  const parsedEvent = transformEventBase(isFromQuote ? service?.eventPreferences ?? eventScheduler : eventScheduler ?? service?.eventPreferences);
  const currentCostOverride = sectorWorkflow?.costOverride || service?.scopeOfWork?.costOverride || {};
  const advancedOverridePrice = Number(currentCostOverride?.price ?? currentCostOverride?.value ?? 0);
  const scopeCostFromQuoteService = Number(service?.scopeOfWork?.costOverride?.value ?? service?.scopeOfWork?.costOverride?.price ?? 0);

  const serviceTotal = Number(service?.total || service?.price || 0);

  const quoteServiceTotal = Number(quoteService?.total || quoteService?.price || 0);

  const quoteServicesTotal = quoteServices.reduce((acc, item) => {
    return acc + Number(item?.total || item?.price || item?.scopeOfWork?.costOverride?.value || item?.scopeOfWork?.costOverride?.price || 0);
  }, 0);

  const totalNum = isAdvanced
    ? advancedOverridePrice || quoteServiceTotal || serviceTotal || scopeCostFromQuoteService || quoteServicesTotal
    : quoteServiceTotal || serviceTotal || quoteServicesTotal;

  const unitsNum = Number(quoteService?.units ?? quoteService?.quantity ?? service?.units ?? 0);

  const unitLabel = quoteService?.measure || service?.measure || "Unit";

  const costPerUnit = unitsNum > 0 ? totalNum / unitsNum : totalNum;

  const hasDepositInvoice = depositQuery.isSuccess && Boolean(depositQuery.data?.depositInvoiceUid);
  const selectedServiceUid = service?.serviceUid ?? null;
  const depositReservedOnLineUid = depositQuery.data?.depositReservedQuoteServiceUid ?? null;

  const depositConsumedByOtherService =
    Boolean(depositReservedOnLineUid) && Boolean(selectedServiceUid) && depositReservedOnLineUid !== selectedServiceUid && depositAvailable <= 0;

  const showDepositPaidCard = hasDepositInvoice && !depositConsumedByOtherService;
  const balanceDue = hasDepositInvoice && totalNum > 0 ? Math.max(0, totalNum - Math.min(depositContractAmount, totalNum)) : totalNum;

  useEffect(() => {
    if (!quote?.id || !depositQuery.isSuccess) return;

    if (!depositQuery.data?.depositInvoiceUid) {
      dispatch(setCreditedDepositAmount(null));
      return;
    }

    const reserved = depositQuery.data?.depositReservedQuoteServiceUid ?? null;
    const sel = service?.serviceUid ?? null;
    const consumedElsewhere = Boolean(reserved) && Boolean(sel) && reserved !== sel && depositAvailable <= 0;

    if (consumedElsewhere) {
      dispatch(setCreditedDepositAmount(null));
      return;
    }

    const cappedForLine = totalNum > 0 ? Math.min(depositAvailable, totalNum) : depositAvailable > 0 ? depositAvailable : 0;
    const applied = cappedForLine > 0 ? cappedForLine : null;

    dispatch(setCreditedDepositAmount(applied));
  }, [
    quote?.id,
    depositQuery.isSuccess,
    depositQuery.data?.depositInvoiceUid,
    depositQuery.data?.depositReservedQuoteServiceUid,
    depositAvailable,
    totalNum,
    service?.serviceUid,
    dispatch,
  ]);

  const openDepositInvoice = () => {
    if (!depositInvoiceUid) return;

    openModal(
      { key: RELATED_DEPOSIT_INVOICE_MODAL_KEY, preventParentTrigger: true },
      <InvoiceDetailsView invoiceUid={depositInvoiceUid} scopeOfWorkUid={quote?.scopeOfWorkUid} />,
    );
  };

  const selectedClient = fullState?.client;
  const quoteClient = quote?.client || null;
  const client = selectedClient && typeof selectedClient === "object" ? selectedClient : quoteClient;
  const clientName = [client?.firstName, client?.lastName].filter(Boolean).join(" ") || client?.alias || client?.businessName || "-";
  const clientEmail = getRecordEmail(client);
  const clientPhone = getRecordPhone(client, true);
  const clientType = client?.type === EServiceType.RESIDENTIAL ? "residential" : client?.type || "-";
  const jobType = isAdvanced ? "Advanced" : "Basic";

  const getServiceLineNotes = (item) => {
    return (
      item?.notes ||
      item?.note ||
      item?.description ||
      item?.scopeOfWork?.notes ||
      item?.scopeOfWork?.note ||
      item?.scopeOfWork?.description ||
      item?.service?.notes ||
      item?.service?.description ||
      "-"
    );
  };

  const serviceNotes = getServiceLineNotes(service);
  const scheduleStatus = fullState?.scheduleLater ? "Schedule later" : parsedEvent ? "Scheduled" : "-";
  const aliasFromJob = useSelector((state) => state.jobCreateStepper.alias || "");
  const aliasFromQuote = quote?.alias || quote?.title || quote?.name || "";
  const aliasJob = aliasFromJob || aliasFromQuote;
  const descriptionJob = useSelector((state) => state.jobCreateStepper.description || "");
  const draftNotes = useSelector((state) => state.jobCreateStepper.notes || []);

  const selectedCoverageArea =
    client?.coverageArea || client?.coverageAreas?.find((area) => area?.isDefault) || client?.coverageAreas?.[0] || quote?.coverageArea || null;
  const coverageAreas = Array.isArray(client?.coverageAreas) ? client.coverageAreas.filter((area) => area?.id != null) : [];
  const canChooseCoverage = clientLocked && !isFromQuote && coverageAreas.length > 1;

  const handleCoverageChange = (event) => {
    const nextArea = coverageAreas.find((area) => String(area.id) === String(event.target.value));
    if (!nextArea || !client || typeof client !== "object") return;
    dispatch(setClient({ ...client, coverageArea: nextArea }));
  };

  const jobLocation = selectedCoverageArea?.address || "-";
  const jobLatitude = selectedCoverageArea?.latitude || null;
  const jobLongitude = selectedCoverageArea?.longitude || null;
  const hasJobCoordinates = Boolean(jobLatitude && jobLongitude);
  const mapSrc = hasJobCoordinates ? `https://maps.google.com/maps?q=${jobLatitude},${jobLongitude}&z=15&output=embed` : null;
  const mapsUrl = hasJobCoordinates ? `https://www.google.com/maps/search/?api=1&query=${jobLatitude},${jobLongitude}` : null;

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingJobAlias, setIsEditingJobAlias] = useState(false);
  const [isEditingFrequency, setIsEditingFrequency] = useState(false);
  const [isEditingServiceNameOverride, setIsEditingServiceNameOverride] = useState(false);
  const [useCustomServiceName, setUseCustomServiceName] = useState(Boolean(serviceNameOverride?.trim()));
  const [hoverTaskUid, setHoverTaskUid] = useState(null);
  const [openNotePreviewUid, setOpenNotePreviewUid] = useState(null);
  const [expandedAreaUid, setExpandedAreaUid] = useState(null);

  const frequencyTypeFromRedux = useSelector((state) => state.jobCreateStepper.frequencyType);
  const frequencyTypeFromScope =
    scopeOfWorkFromState?.frequencyType ||
    service?.frequencyType ||
    service?.scopeOfWork?.frequencyType ||
    service?.service?.scopeOfWork?.frequencyType ||
    quoteServices[0]?.frequencyType;
  const isSingleVisit = parsedEvent?.mode === "SINGLE_EXECUTION";
  const currentFrequencyType = isSingleVisit ? EFrequencyType.ONE_TIME : frequencyTypeFromRedux || frequencyTypeFromScope || EFrequencyType.WEEKLY;
  console.log("frequency: ", frequencyTypeFromScope);

  const getAreaQuantity = (area) => {
    return (area?.squareFootages || []).reduce((acc, item) => {
      return acc + (Number(item?.quantity) || 0);
    }, 0);
  };

  const formatMonetaryAmountBy = (value) => {
    const match = monetaryAmountOptions.find((item) => item.value === value);
    return match?.label || value;
  };

  const money = (value) => {
    const amount = Number(value || 0);

    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const areas = Array.isArray(sectorWorkflow?.areas) ? sectorWorkflow.areas : [];

  const activeAreas = areas.filter((area) => {
    const quantity = getAreaQuantity(area);
    return quantity > 0;
  });

  const otherAreas = Array.isArray(sectorWorkflow?.otherAreas) ? sectorWorkflow.otherAreas : [];

  const activeOtherAreas = otherAreas.filter((area) => {
    const quantity = getAreaQuantity(area);
    return quantity > 0;
  });

  const selectedObjects =
    Array.isArray(sectorWorkflow?.objects) && sectorWorkflow.objects.length > 0
      ? sectorWorkflow.objects
      : Array.isArray(sectorWorkflow?.additionalObjects)
        ? sectorWorkflow.additionalObjects
        : [];

  const specialRequests = Array.isArray(sectorWorkflow?.specialRequests) ? sectorWorkflow.specialRequests : [];
  const totalAreas = activeAreas.length + activeOtherAreas.length;

  const categoryName =
    getName(sectorWorkflow?.category) ||
    getName(sectorWorkflow?.categories) ||
    sectorWorkflow?.categoryName ||
    getName(service?.scopeOfWork?.category) ||
    getName(service?.scopeOfWork?.categories) ||
    service?.scopeOfWork?.categoryName ||
    getName(quoteServices[0]?.scopeOfWork?.category) ||
    getName(quoteServices[0]?.scopeOfWork?.categories) ||
    quoteServices[0]?.scopeOfWork?.categoryName ||
    "-";

  const typeOfPlaceName =
    getName(sectorWorkflow?.places) ||
    getName(sectorWorkflow?.place) ||
    getName(sectorWorkflow?.typeOfPlace) ||
    getName(sectorWorkflow?.typeOfPlaces) ||
    sectorWorkflow?.typeOfPlaceName ||
    getName(service?.scopeOfWork?.places) ||
    getName(service?.scopeOfWork?.place) ||
    getName(service?.scopeOfWork?.typeOfPlace) ||
    getName(service?.scopeOfWork?.typeOfPlaces) ||
    service?.scopeOfWork?.typeOfPlaceName ||
    getName(quoteServices[0]?.scopeOfWork?.places) ||
    getName(quoteServices[0]?.scopeOfWork?.place) ||
    getName(quoteServices[0]?.scopeOfWork?.typeOfPlace) ||
    getName(quoteServices[0]?.scopeOfWork?.typeOfPlaces) ||
    quoteServices[0]?.scopeOfWork?.typeOfPlaceName ||
    "-";

  const isResidentialCategory = normalizeLabel(categoryName) === "residential";
  const shouldShowSpecialRequests = isAdvanced && isResidentialCategory;

  const squareFeet = sectorWorkflow?.squareft || sectorWorkflow?.squareFootage || "-";
  const averagePeople = sectorWorkflow?.averagePeople ?? "-";

  const editScopeStep = (scopeStep) => {
    goToStepByKey("scopeOfWork", {
      returnToSummary: true,
      scopeStep,
    });
  };

  const handleCostOverrideTypeChange = (event) => {
    dispatch(
      setScopeOfWorkCostOverride({
        ...currentCostOverride,
        by: event.target.value,
        currency: currentCostOverride?.currency || "USD",
      }),
    );
  };

  const handleCostOverridePriceChange = (event) => {
    const rawValue = event.target.value;

    dispatch(
      setScopeOfWorkCostOverride({
        ...currentCostOverride,
        by: currentCostOverride?.by || EMonetaryAmountBy.UNIQUE,
        price: rawValue === "" ? null : Number(rawValue),
        currency: currentCostOverride?.currency || "USD",
      }),
    );
  };

  const originalAdvancedServiceName =
    getName(sectorWorkflow?.typeOfService) || getName(sectorWorkflow?.service) || getName(service?.service) || "Service";

  const displayedAdvancedServiceName = useCustomServiceName && serviceNameOverride?.trim() ? serviceNameOverride.trim() : originalAdvancedServiceName;
  useEffect(() => {
    setUseCustomServiceName(Boolean(serviceNameOverride?.trim()));
  }, [serviceNameOverride]);

  return (
    <Grid
      container
      spacing={3}
      sx={{
        p: 2,
        alignItems: "flex-start",
      }}
    >
      <Grid item xs={12} lg={isAdvanced ? 8 : 12}>
        <Card sx={{ mt: 2, border: "none", borderRadius: 2, mb: 2 }}>
          <EditableSectionHeader
            title="Client"
            disabled={isFromQuote || clientLocked}
            onEdit={() => {
              goToStepByKey("selectClient", { returnToSummary: true });
            }}
          />

          <Row label="Name" value={clientName} />
          <Row label="Type" value={clientType} />
          <Row label="Phone" value={clientPhone} />
          <Row label="Email" value={clientEmail} textTransform="none" />
        </Card>

        <Card sx={{ border: "none", borderRadius: 2 }}>
          <Typography variant="h6">Job Overview</Typography>

          <ServiceNameOverviewRow
            value={aliasJob}
            isEditing={isEditingJobAlias}
            disabled={isFromQuote}
            onEdit={() => setIsEditingJobAlias(true)}
            onCancel={() => setIsEditingJobAlias(false)}
            onChange={(value) => {
              dispatch(setAlias(value));
            }}
          />

          <EditableInfoRow
            label="Job type"
            disabled={isFromQuote}
            value={jobType}
            onEdit={() => {
              goToStepByKey("quoteMode", {
                returnToSummary: true,
              });
            }}
          />

          <FrequencyOverviewRow
            value={currentFrequencyType}
            isEditing={isEditingFrequency}
            disabled={isFromQuote || isSingleVisit}
            onEdit={() => setIsEditingFrequency(true)}
            onCancel={() => setIsEditingFrequency(false)}
            onChange={(value) => {
              dispatch(setFrequencyType(value));
            }}
          />

          <Row label="Status" value={quote?.status} fallback="Draft" />

          <Box sx={{ mt: 1.5 }}>
            <DescriptionSection
              descriptionJob={descriptionJob}
              isEditingDescription={isEditingDescription}
              setIsEditingDescription={setIsEditingDescription}
              onChangeDescription={(value) => {
                dispatch(setDescription(value));
              }}
            />
          </Box>

          {!isUpdateJob && (
            <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${colors.gray?.[100] || "var(--fx-surface-border, #E2E8F0)"}` }}>
              <JobNotesCard
                mode="draft"
                maxNotes={1}
                clientUid={client?.uid}
                draftNotes={draftNotes}
                onDraftNotesChange={(notes) => dispatch(setNotes(notes))}
                sx={{ px: 0, py: 0 }}
              />
            </Box>
          )}
        </Card>

        {!isAdvanced ? (
          <Card sx={{ mt: 2, border: "none", borderRadius: 2 }}>
            <EditableSectionHeader title="Scope of Work" disabled={isFromQuote} onEdit={() => editScopeStep(ScopeOfWorkStep.TypeOfService)} />

            <ServiceNameOverrideRow
              originalServiceName={originalServiceName}
              value={serviceNameOverride}
              isEditing={isEditingServiceNameOverride}
              disabled={isFromQuote}
              onEdit={() => setIsEditingServiceNameOverride(true)}
              onCancel={() => setIsEditingServiceNameOverride(false)}
              onChange={(value) => {
                dispatch(setServiceNameOverride(value));
              }}
            />
            <Row label={`Quantity (${unitLabel})`} value={unitsNum} fallback={0} />
            <Row label="Schedule" value={scheduleStatus} />
            <Row label="Notes" value={serviceNotes} hideIfEmpty />
            <Row label={`Cost per ${unitLabel}`} value={money(costPerUnit)} />
          </Card>
        ) : null}

        {serviceLines.length > 1 ? (
          <Card sx={{ mt: 2, border: "none", borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>Quote Services</Typography>

            <Stack gap={1}>
              {serviceLines.map((item, index) => {
                const itemName = getServiceLineName(item, index);
                const itemTotal = Number(
                  item?.total || item?.price || item?.scopeOfWork?.costOverride?.value || item?.scopeOfWork?.costOverride?.price || 0,
                );
                const itemNotes = getServiceLineNotes(item);

                return (
                  <Box
                    key={item?.id || item?.serviceUid || item?.uid || index}
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: alphaVar(colors.orange.base, 0.04),
                      border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                    }}
                  >
                    <Row label={`Service ${index + 1}`} value={itemName} />
                    <Row label="Quantity" value={item?.units} fallback={0} />
                    <Row label="Total" value={money(itemTotal)} />
                    <Row label="Notes" value={itemNotes} hideIfEmpty />
                  </Box>
                );
              })}
            </Stack>
          </Card>
        ) : null}
        {isAdvanced ? (
          <Card sx={{ mt: 2, border: "none", borderRadius: 2 }}>
            <Box sx={{ mb: 1.25 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                  letterSpacing: 0.5,
                }}
              >
                Scope of Work
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                  mt: 0.25,
                }}
              >
                Review each work section and edit it directly.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 1.25,
              }}
            >
              <ScopeSummaryCard
                title="Category"
                subtitle="Service category"
                disabled={isFromQuote}
                value={categoryName}
                onEdit={() => editScopeStep(ScopeOfWorkStep.Categories)}
              />

              <ScopeSummaryCard
                title="Type of Place"
                disabled={isFromQuote}
                subtitle={`${squareFeet || "-"} sq ft `}
                value={typeOfPlaceName}
                onEdit={() => editScopeStep(ScopeOfWorkStep.TypeOfPlaces)}
              />

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alphaVar(colors.orange.base, 0.035),
                  border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                  minWidth: 0,
                  gridColumn: {
                    xs: "1",
                    sm: "span 2",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                        color: colors.orange.base,
                        mb: 0.5,
                      }}
                    >
                      Service
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                        lineHeight: 1.35,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {displayedAdvancedServiceName}
                    </Typography>

                    {useCustomServiceName && serviceNameOverride?.trim() ? (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                          mt: 0.35,
                        }}
                      >
                        Original service: {originalAdvancedServiceName}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                          mt: 0.35,
                        }}
                      >
                        Selected service type
                      </Typography>
                    )}
                  </Box>

                  {!isFromQuote ? (
                    <Tooltip title="Change service type">
                      <IconButton
                        onClick={() => editScopeStep(ScopeOfWorkStep.TypeOfService)}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.25,
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          border: `1px solid ${alphaVar(colors.orange.base, 0.16)}`,
                          color: colors.orange.base,
                          flexShrink: 0,
                          "&:hover": {
                            bgcolor: alphaVar(colors.orange.base, 0.1),
                          },
                        }}
                      >
                        <Edit sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>

                {!isFromQuote ? (
                  <ServiceAliasField
                    enabled={useCustomServiceName}
                    value={serviceNameOverride || ""}
                    originalServiceName={originalAdvancedServiceName}
                    disabled={isFromQuote}
                    size="small"
                    onEnabledChange={setUseCustomServiceName}
                    onValueChange={(value) => {
                      dispatch(setServiceNameOverride(value));
                    }}
                  />
                ) : null}
              </Box>

              <ScopeSummaryCard
                title="Areas & Tasks"
                disabled={isFromQuote}
                subtitle="Selected work areas"
                value={`${totalAreas || 0} areas`}
                onEdit={() => editScopeStep(ScopeOfWorkStep.Areas)}
              />
              <ScopeSummaryCard
                title="Other Areas"
                disabled={isFromQuote}
                subtitle="Additional work areas"
                value={`${activeOtherAreas.length || 0} other areas`}
                onEdit={() => editScopeStep(ScopeOfWorkStep.Areas)}
              />
              <ScopeSummaryCard
                title="Objects"
                disabled={isFromQuote}
                subtitle="Selected objects"
                value={`${selectedObjects.length || 0} objects`}
                onEdit={() => editScopeStep(ScopeOfWorkStep.Objects)}
              />

              {shouldShowSpecialRequests && (
                <ScopeSummaryCard
                  title="Special Requests"
                  disabled={isFromQuote}
                  subtitle="Extra service requests"
                  value={`${specialRequests.length || 0} requests`}
                  onEdit={() => editScopeStep(ScopeOfWorkStep.SpecialRequests)}
                />
              )}
            </Box>
          </Card>
        ) : null}

        <Card sx={{ mt: 2, border: "none", borderRadius: 2 }}>
          <EditableSectionHeader
            title="Location"
            disabled={isFromQuote || (clientLocked && !canChooseCoverage)}
            onEdit={() => {
              if (clientLocked) {
                goToStepByKey("selectClientAddress", { returnToSummary: true });
                return;
              }
              goToStepByKey("selectClient", { returnToSummary: true });
            }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: hasJobCoordinates ? "1.15fr 0.85fr" : "1fr",
              },
              gap: 1.25,
              p: 1.25,
              borderRadius: 2,
              bgcolor: alphaVar(colors.orange.base, 0.04),
              border: `1px solid ${alphaVar(colors.orange.base, 0.12)}`,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                    mb: 0.5,
                  }}
                >
                  Job address
                </Typography>

                {canChooseCoverage ? (
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedCoverageArea?.id != null ? String(selectedCoverageArea.id) : ""}
                      onChange={handleCoverageChange}
                      MenuProps={menuPropsModal}
                      sx={{
                        fontSize: 13,
                        borderRadius: 2,
                        bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      }}
                    >
                      {coverageAreas.map((area) => (
                        <MenuItem key={area.id} value={String(area.id)}>
                          {area.name || area.address || `Coverage ${area.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: colors.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
                      lineHeight: 1.5,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {jobLocation}
                  </Typography>
                )}
              </Box>

              {hasJobCoordinates && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                    mt: 0.5,
                  }}
                >
                  <Link
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: colors.orange.base,
                      px: 1,
                      py: 0.35,
                      borderRadius: 1,
                      bgcolor: alphaVar(colors.orange.base, 0.08),
                      "&:hover": {
                        bgcolor: alphaVar(colors.orange.base, 0.14),
                      },
                    }}
                  >
                    Open map
                  </Link>
                </Box>
              )}
            </Box>

            {hasJobCoordinates && (
              <Box
                sx={{
                  position: "relative",
                  height: 150,
                  minHeight: 150,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${alphaVar(colors.orange.base, 0.16)}`,
                  bgcolor: colors.gray?.[50] || "var(--fx-surface-sunken, #F8FAFC)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                }}
              >
                <iframe
                  title="Job location map"
                  src={mapSrc}
                  width="100%"
                  height="100%"
                  style={{
                    border: 0,
                    display: "block",
                  }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />

                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    px: 1,
                    py: 0.35,
                    borderRadius: 999,
                    bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.92)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: colors.orange.base,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Job site
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Card>

        <Card sx={{ mt: 2, border: "none", borderRadius: 2 }}>
          {showDepositPaidCard ? (
            <Box
              sx={{
                bgcolor: alphaVar(colors.success[100], 0.3),
                p: 1.5,
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: colors.success[600],
                    textTransform: "uppercase",
                  }}
                >
                  Deposit Paid
                </Typography>

                {depositInvoiceUid && (
                  <Link
                    component="button"
                    type="button"
                    onClick={openDepositInvoice}
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: colors.blue[600],
                    }}
                  >
                    View deposit invoice
                  </Link>
                )}
              </Box>

              <Row
                label="Deposit Amount"
                value={
                  <Typography sx={{ fontWeight: 600, color: colors.success[600] }}>
                    {money(depositQuery.data?.depositAmount ?? quote?.depositAmount ?? 0)}
                  </Typography>
                }
              />

              <Row label="Remaining" value={<Typography sx={{ fontWeight: 700, color: colors.orange.base }}>{money(balanceDue)}</Typography>} />
            </Box>
          ) : (
            <Box
              sx={{
                bgcolor: alphaVar(colors.orange.base, 0.05),
                p: 1,
                borderRadius: 1,
              }}
            >
              <Row label="Total cost" value={<Typography sx={{ fontWeight: 700, color: colors.orange.base }}>{money(totalNum)}</Typography>} />

              {depositConsumedByOtherService ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  The paid deposit is linked to another service line on this quote; it does not apply to this line.
                </Typography>
              ) : null}
            </Box>
          )}
        </Card>

        <Card sx={{ mt: 2, border: "none", borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 2, fontSize: 14 }}>Selected Reminders</Typography>

          {Object.values(fullState.selectedReminders || {}).some((r) => r !== null) ? (
            <Stack gap={1.5}>
              {Object.entries(fullState.selectedReminders).map(([category, reminder]) => {
                if (!reminder) return null;

                return (
                  <Box
                    key={category}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alphaVar(colors.orange.base, 0.05),
                      border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        color: colors.orange.base,
                        mb: 0.5,
                      }}
                    >
                      Target: {category}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: colors.gray[800],
                      }}
                    >
                      {reminder.name}
                    </Typography>

                    <Stack direction="row" gap={1} sx={{ mt: 1 }}>
                      {reminder.sendSms && <ChannelChip label="SMS" active={true} />}
                      {reminder.sendEmail && <ChannelChip label="EMAIL" active={true} />}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography
              sx={{
                fontSize: 13,
                color: colors.gray[400],
                fontStyle: "italic",
              }}
            >
              No reminders selected for this job.
            </Typography>
          )}
        </Card>

        {isAdvanced ? (
          <Card sx={{ mt: 2, border: "none", borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>Areas & Tasks</Typography>

            {activeAreas.length > 0 ? (
              <Stack gap={1}>
                {activeAreas.map((area, idx) => {
                  const areaUid = String(area?.uid ?? idx);
                  const areaQuantity = getAreaQuantity(area);
                  const areaTasks = Array.isArray(area?.tasks) ? area.tasks : [];
                  const areaNotesCount = areaTasks.filter((task) => String(task?.note ?? "").trim().length > 0).length;
                  const isAreaOpen = expandedAreaUid === areaUid;

                  return (
                    <Box
                      key={areaUid}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                        bgcolor: "var(--fx-surface-base, #FFFFFF)",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        onClick={() => setExpandedAreaUid(isAreaOpen ? null : areaUid)}
                        sx={{
                          p: 1.25,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          cursor: "pointer",
                          bgcolor: alphaVar(colors.orange.base, 0.035),
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              color: colors.gray[800],
                              lineHeight: 1.25,
                            }}
                          >
                            {area?.name || "Unnamed area"}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 11,
                              color: colors.gray[500],
                              mt: 0.25,
                            }}
                          >
                            {areaQuantity > 0 ? `${areaQuantity} ${unitLabel} · ` : ""}
                            {areaTasks.length} tasks
                            {` · ${areaNotesCount} notes`}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: "var(--fx-surface-base, #FFFFFF)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: colors.orange.base,
                            flexShrink: 0,
                          }}
                        >
                          {isAreaOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </Box>
                      </Box>

                      <Collapse in={isAreaOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 1.25, py: 1 }}>
                          {areaTasks.length > 0 ? (
                            <Stack gap={0.5}>
                              {areaTasks.map((task, tIdx) => {
                                const taskUid = String(task?.uid ?? `${areaUid}-${tIdx}`);
                                const taskName = task?.name ?? task?.task?.name ?? task?.description ?? "Unnamed task";
                                const note = String(task?.note ?? "").trim();
                                const hasNote = note.length > 0;
                                const isHovering = hoverTaskUid === taskUid;
                                const isNoteOpen = openNotePreviewUid === taskUid;

                                return (
                                  <Box
                                    key={taskUid}
                                    sx={{
                                      px: 0.75,
                                      py: 0.35,
                                      borderRadius: 1.25,
                                      "&:hover": {
                                        bgcolor: alphaVar(colors.orange.base, 0.035),
                                      },
                                    }}
                                    onMouseEnter={() => setHoverTaskUid(taskUid)}
                                    onMouseLeave={() => setHoverTaskUid(null)}
                                  >
                                    <Box
                                      sx={{
                                        minHeight: 34,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          color: colors.gray[700],
                                          flex: 1,
                                          minWidth: 0,
                                        }}
                                      >
                                        {taskName}
                                      </Typography>

                                      <Box
                                        sx={{
                                          width: 34,
                                          minWidth: 34,
                                          height: 30,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {hasNote ? (
                                          <Tooltip title={isNoteOpen ? "Hide note" : "View note"} arrow placement="top">
                                            <ChatBubble
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenNotePreviewUid(isNoteOpen ? null : taskUid);
                                              }}
                                              sx={{
                                                fontSize: 16,
                                                color: isNoteOpen ? colors.orange[700] : colors.orange.base,
                                                cursor: "pointer",
                                              }}
                                            />
                                          </Tooltip>
                                        ) : (
                                          <Box
                                            sx={{
                                              opacity: isHovering ? 1 : 0,
                                              pointerEvents: isHovering ? "auto" : "none",
                                              transition: "opacity 120ms ease",
                                            }}
                                          >
                                            <TaskNoteBubble
                                              areaUid={area?.uid}
                                              taskId={taskUid}
                                              taskName={taskName}
                                              noteFromStore={task?.note ?? ""}
                                              icon={<AddCircleOutline fontSize="small" />}
                                            />
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>

                                    <Collapse in={hasNote && isNoteOpen} timeout="auto" unmountOnExit>
                                      <Box
                                        sx={{
                                          mt: 0.5,
                                          mb: 0.5,
                                          p: 1,
                                          borderRadius: 1.5,
                                          bgcolor: alphaVar(colors.orange.base, 0.055),
                                          border: `1px solid ${alphaVar(colors.orange.base, 0.14)}`,
                                          display: "flex",
                                          alignItems: "flex-start",
                                          justifyContent: "space-between",
                                          gap: 1,
                                          minWidth: 0,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: 12,
                                            color: colors.gray[600],
                                            lineHeight: 1.5,
                                            whiteSpace: "pre-wrap",
                                            overflowWrap: "anywhere",
                                            wordBreak: "break-word",
                                            flex: 1,
                                            minWidth: 0,
                                          }}
                                        >
                                          {note}
                                        </Typography>

                                        <Box
                                          sx={{
                                            width: 30,
                                            minWidth: 30,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mt: -0.5,
                                            flexShrink: 0,
                                          }}
                                        >
                                          <TaskNoteBubble
                                            areaUid={area?.uid}
                                            taskId={taskUid}
                                            taskName={taskName}
                                            noteFromStore={task?.note ?? ""}
                                            icon={<Edit fontSize="small" />}
                                          />
                                        </Box>
                                      </Box>
                                    </Collapse>
                                  </Box>
                                );
                              })}
                            </Stack>
                          ) : (
                            <Typography sx={{ fontSize: 13, color: colors.gray[500] }}>No tasks assigned.</Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: 13, color: colors.gray[500] }}>No areas assigned.</Typography>
            )}
          </Card>
        ) : null}

        {isAdvanced && activeOtherAreas.length > 0 ? (
          <Card
            sx={{
              mt: 2,
              border: "none",
              borderRadius: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: 14,
              }}
            >
              Other Areas
            </Typography>

            <Stack gap={1}>
              {activeOtherAreas.map((area, index) => {
                const areaUid = String(area?.uid ?? area?.id ?? `other-area-${index}`);

                const areaQuantity = getAreaQuantity(area);

                const areaTasks = Array.isArray(area?.tasks) ? area.tasks : [];

                const areaNotesCount = areaTasks.filter((task) => String(task?.note ?? "").trim().length > 0).length;

                const areaStateUid = `other:${areaUid}`;

                const isAreaOpen = expandedAreaUid === areaStateUid;

                return (
                  <Box
                    key={areaUid}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={() => setExpandedAreaUid(isAreaOpen ? null : areaStateUid)}
                      sx={{
                        p: 1.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        cursor: "pointer",
                        bgcolor: alphaVar(colors.orange.base, 0.035),
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: colors.gray[800],
                            lineHeight: 1.25,
                          }}
                        >
                          {area?.name || "Unnamed other area"}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 11,
                            color: colors.gray[500],
                            mt: 0.25,
                          }}
                        >
                          {areaQuantity > 0 ? `${areaQuantity} ${unitLabel} · ` : ""}
                          {areaTasks.length} {areaTasks.length === 1 ? "task" : "tasks"}
                          {` · ${areaNotesCount} ${areaNotesCount === 1 ? "note" : "notes"}`}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: colors.orange.base,
                          flexShrink: 0,
                        }}
                      >
                        {isAreaOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </Box>
                    </Box>

                    <Collapse in={isAreaOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 1.25, py: 1 }}>
                        {areaTasks.length > 0 ? (
                          <Stack gap={0.5}>
                            {areaTasks.map((task, taskIndex) => {
                              const taskUid = String(task?.uid ?? task?.id ?? `${areaUid}-${taskIndex}`);

                              const taskName = task?.name ?? task?.task?.name ?? task?.description ?? "Unnamed task";

                              const note = String(task?.note ?? "").trim();

                              const hasNote = note.length > 0;

                              const taskStateUid = `${areaStateUid}:${taskUid}`;

                              const isHovering = hoverTaskUid === taskStateUid;

                              const isNoteOpen = openNotePreviewUid === taskStateUid;

                              return (
                                <Box
                                  key={taskUid}
                                  sx={{
                                    px: 0.75,
                                    py: 0.35,
                                    borderRadius: 1.25,
                                    "&:hover": {
                                      bgcolor: alphaVar(colors.orange.base, 0.035),
                                    },
                                  }}
                                  onMouseEnter={() => setHoverTaskUid(taskStateUid)}
                                  onMouseLeave={() => setHoverTaskUid(null)}
                                >
                                  <Box
                                    sx={{
                                      minHeight: 34,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        color: colors.gray[700],
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      {taskName}
                                    </Typography>

                                    <Box
                                      sx={{
                                        width: 34,
                                        minWidth: 34,
                                        height: 30,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {hasNote ? (
                                        <Tooltip title={isNoteOpen ? "Hide note" : "View note"} arrow placement="top">
                                          <ChatBubble
                                            onClick={(event) => {
                                              event.stopPropagation();

                                              setOpenNotePreviewUid(isNoteOpen ? null : taskStateUid);
                                            }}
                                            sx={{
                                              fontSize: 16,
                                              color: isNoteOpen ? colors.orange[700] : colors.orange.base,
                                              cursor: "pointer",
                                            }}
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Box
                                          sx={{
                                            opacity: isHovering ? 1 : 0,
                                            pointerEvents: isHovering ? "auto" : "none",
                                            transition: "opacity 120ms ease",
                                          }}
                                        >
                                          <TaskNoteBubble
                                            areaUid={area?.uid}
                                            taskId={taskUid}
                                            taskName={taskName}
                                            noteFromStore={task?.note ?? ""}
                                            icon={<AddCircleOutline fontSize="small" />}
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>

                                  <Collapse in={hasNote && isNoteOpen} timeout="auto" unmountOnExit>
                                    <Box
                                      sx={{
                                        mt: 0.5,
                                        mb: 0.5,
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: alphaVar(colors.orange.base, 0.055),
                                        border: `1px solid ${alphaVar(colors.orange.base, 0.14)}`,
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          color: colors.gray[600],
                                          lineHeight: 1.5,
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                          wordBreak: "break-word",
                                          flex: 1,
                                          minWidth: 0,
                                        }}
                                      >
                                        {note}
                                      </Typography>

                                      <Box
                                        sx={{
                                          width: 30,
                                          minWidth: 30,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          mt: -0.5,
                                          flexShrink: 0,
                                        }}
                                      >
                                        <TaskNoteBubble
                                          areaUid={area?.uid}
                                          taskId={taskUid}
                                          taskName={taskName}
                                          noteFromStore={task?.note ?? ""}
                                          icon={<Edit fontSize="small" />}
                                        />
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: colors.gray[500],
                            }}
                          >
                            No tasks assigned.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        ) : null}

        {quote?.contractOverride?.length > 0 && (
          <Card sx={{ mt: 2 }}>
            <SectionTitle>Contract Override</SectionTitle>

            {quote.contractOverride.map((c, idx) => (
              <Card key={idx} sx={{ mb: 1, bgcolor: "var(--fx-surface-base, #FFFFFF)" }}>
                <Row label="Start Date" value={new Date(c.startDate).toLocaleString()} />
                <Row label="End Date" value={new Date(c.endDate).toLocaleString()} />

                {c.hours?.map((h, i) => (
                  <Row key={i} label={`Hour ${i + 1}`} value={`${h.startHour} - ${h.endHour}`} />
                ))}
              </Card>
            ))}
          </Card>
        )}

        {isAdvanced && selectedObjects.length > 0 && (
          <Card sx={{ mt: 2, border: "none", borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 14 }}>Objects</Typography>

            <ObjectGrid items={selectedObjects} fallbackAlt="Object" fallbackName="Unnamed object" />
          </Card>
        )}

        {shouldShowSpecialRequests && specialRequests.length > 0 && (
          <Card sx={{ mt: 2, border: "none", borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 14 }}>Special Requests</Typography>

            <ObjectGrid items={specialRequests} fallbackAlt="Special request" fallbackName="Special request" />
          </Card>
        )}
      </Grid>

      {isAdvanced ? (
        <Grid
          item
          xs={12}
          lg={4}
          sx={{
            position: { lg: "sticky" },
            top: { lg: 7 },
            alignSelf: "flex-start",
          }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alphaVar(colors.orange.base, 0.08)}, 0 2px 8px rgba(0,0,0,0.04)`,
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
              }}
            >
              <Box
                sx={{
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                }}
              />

              <Box sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: alphaVar(colors.orange.base, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                        stroke={colors.orange.base}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                        lineHeight: 1.2,
                      }}
                    >
                      Pricing
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 12,
                        color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                        mt: 0.25,
                      }}
                    >
                      Override the default pricing for this job
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                          mb: 0.75,
                        }}
                      >
                        Pricing Type
                      </Typography>

                      <Select
                        MenuProps={menuPropsModal}
                        size="small"
                        displayEmpty
                        value={currentCostOverride?.by || EMonetaryAmountBy.UNIQUE}
                        onChange={handleCostOverrideTypeChange}
                        renderValue={(selected) => {
                          if (!selected) return "Select type";
                          return monetaryAmountOptions.find((option) => option.value === selected)?.label || selected;
                        }}
                        sx={{
                          borderRadius: 2,
                          bgcolor: colors.gray?.[50] || "var(--fx-surface-sunken, #F8FAFC)",
                          fontSize: 13,
                          color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                          transition: "all 0.15s",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: colors.gray?.[200] || "var(--fx-surface-border, #E2E8F0)",
                          },
                          "& .MuiSelect-icon": {
                            color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                          },
                          "&:hover": {
                            bgcolor: alphaVar(colors.orange.base, 0.04),
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: alphaVar(colors.orange.base, 0.4),
                          },
                          "&.Mui-focused": {
                            bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: colors.orange.base,
                            borderWidth: "1px",
                          },
                          "&.Mui-focused .MuiSelect-icon": {
                            color: colors.orange.base,
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select type
                        </MenuItem>

                        {monetaryAmountOptions.map((option) => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                              color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                              "&.Mui-selected": {
                                backgroundColor: alphaVar(colors.orange.base, 0.12),
                              },
                              "&.Mui-selected:hover": {
                                backgroundColor: alphaVar(colors.orange.base, 0.18),
                              },
                            }}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        sx={{
                          fontWeight: 600,
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                          mb: 0.75,
                        }}
                      >
                        Amount (USD)
                      </FormLabel>

                      <TextField
                        type="number"
                        size="small"
                        value={currentCostOverride?.price === null ? "" : (currentCostOverride?.price ?? totalNum)}
                        onChange={handleCostOverridePriceChange}
                        placeholder="0.00"
                        inputProps={{ min: 0, step: "0.01" }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: colors.gray?.[50] || "var(--fx-surface-sunken, #F8FAFC)",
                            fontSize: 13,
                            transition: "all 0.15s",
                            "&:hover": {
                              bgcolor: alphaVar(colors.orange.base, 0.04),
                              "& fieldset": {
                                borderColor: alphaVar(colors.orange.base, 0.4),
                              },
                            },
                            "&.Mui-focused": {
                              bgcolor: "var(--fx-surface-base, #FFFFFF)",
                              "& fieldset": {
                                borderColor: colors.orange.base,
                              },
                            },
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                {(currentCostOverride?.by || currentCostOverride?.price != null) && (
                  <Box
                    sx={{
                      mt: 2.5,
                      p: 2,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alphaVar(colors.orange.base, 0.07)} 0%, ${alphaVar(colors.orange.base, 0.03)} 100%)`,
                      border: `1px solid ${alphaVar(colors.orange.base, 0.15)}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          color: colors.orange.base,
                          mb: 0.25,
                        }}
                      >
                        Override Active
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 13,
                          color: colors.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
                        }}
                      >
                        {formatMonetaryAmountBy(currentCostOverride?.by || EMonetaryAmountBy.UNIQUE) || "—"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        textAlign: "right",
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: colors.orange.base,
                        boxShadow: `0 4px 12px ${alphaVar(colors.orange.base, 0.35)}`,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 800,
                          color: "#fff",
                          lineHeight: 1,
                        }}
                      >
                        {money(totalNum)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>
        </Grid>
      ) : null}
    </Grid>
  );
};

const SectionTitle = ({ children, icon = true }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      {icon && (
        <Box
          sx={{
            width: 4,
            height: 24,
            bgcolor: colors.orange.base,
            borderRadius: 2,
            mr: 2,
          }}
        />
      )}

      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
          letterSpacing: 0.5,
        }}
      >
        {children}
      </Typography>
    </Box>

    <Divider sx={{ borderColor: colors.gray?.[200] || "var(--fx-surface-border, #E2E8F0)", opacity: 0.8 }} />
  </Box>
);

const safeRenderValue = (value, fallback = "N/A") => {
  const isEmpty = value === undefined || value === null || value === "" || value === "-";

  if (isEmpty) return fallback;

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (value?.props) {
    return value;
  }

  if (typeof value === "object") {
    return value.name || value.label || value.title || value.type || fallback;
  }

  return fallback;
};

const Row = ({ label, value, fallback = "N/A", hideIfEmpty = false, textTransform = "capitalize" }) => {
  const finalValue = safeRenderValue(value, fallback);
  const isEmpty = finalValue === fallback;

  if (hideIfEmpty && isEmpty) return null;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        py: 0.5,
        gap: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          textTransform: "capitalize",
          color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          textAlign: "right",
          textTransform,
        }}
      >
        {finalValue}
      </Typography>
    </Box>
  );
};

const ChannelChip = ({ label, active }) => (
  <Box
    sx={{
      px: 1,
      py: 0.2,
      fontSize: 10,
      fontWeight: 800,
      borderRadius: "5px",
      backgroundColor: active ? colors.orange[100] : colors.gray[100],
      color: active ? colors.orange[700] : colors.gray[400],
    }}
  >
    {label}
  </Box>
);

const Card = ({ children, sx }) => (
  <Box
    sx={{
      p: 2,
      border: `1px solid ${colors.gray[100]}`,
      borderRadius: 2,
      bgcolor: "var(--fx-surface-base, #FFFFFF)",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const normalizeLabel = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const EditableSectionHeader = ({ title, onEdit, disabled = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      mb: 1,
    }}
  >
    <Typography
      variant="h6"
      sx={{
        fontWeight: 600,
        color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
        letterSpacing: 0.5,
      }}
    >
      {title}
    </Typography>

    {!disabled && (
      <Tooltip title="Edit">
        <Box
          onClick={onEdit}
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: alphaVar(colors.orange.base, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: colors.orange.base,
            cursor: "pointer",
            flexShrink: 0,
            "&:hover": {
              bgcolor: alphaVar(colors.orange.base, 0.14),
            },
          }}
        >
          <Edit fontSize="small" />
        </Box>
      </Tooltip>
    )}
  </Box>
);
const ScopeSummaryCard = ({ title, subtitle, value, onEdit, disabled = false, sx }) => {
  const isEmpty = value === undefined || value === null || value === "" || value === "-";
  const finalValue = isEmpty ? "Not selected" : value;

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: alphaVar(colors.orange.base, 0.035),
        border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.25,
        minWidth: 0,
        transition: "all 140ms ease",
        "&:hover": {
          bgcolor: alphaVar(colors.orange.base, 0.055),
          borderColor: alphaVar(colors.orange.base, 0.18),
        },
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: colors.orange.base,
            mb: 0.35,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
            lineHeight: 1.35,
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {finalValue}
        </Typography>

        {subtitle ? (
          <Typography
            sx={{
              fontSize: 11,
              color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
              mt: 0.35,
              lineHeight: 1.35,
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      {!disabled && (
        <Tooltip title="Edit">
          <Box
            onClick={onEdit}
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              border: `1px solid ${alphaVar(colors.orange.base, 0.16)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.orange.base,
              cursor: "pointer",
              flexShrink: 0,
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              "&:hover": {
                bgcolor: alphaVar(colors.orange.base, 0.1),
              },
            }}
          >
            <Edit sx={{ fontSize: 15 }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const EditableInfoRow = ({ label, value, fallback = "N/A", onEdit, disabled = false }) => {
  const isEmpty = value === undefined || value === null || value === "" || value === "-";
  const finalValue = isEmpty ? fallback : value;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
        gap: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          textTransform: "capitalize",
          color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            textAlign: "right",
            textTransform: "capitalize",
            minWidth: 0,
          }}
        >
          {finalValue}
        </Typography>

        {!disabled && (
          <Tooltip title="Edit">
            <Box
              onClick={onEdit}
              sx={{
                width: 24,
                height: 24,
                borderRadius: 1,
                bgcolor: alphaVar(colors.orange.base, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: colors.orange.base,
                cursor: "pointer",
                flexShrink: 0,
                "&:hover": {
                  bgcolor: alphaVar(colors.orange.base, 0.14),
                },
              }}
            >
              <Edit sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const ServiceNameOverviewRow = ({ value, isEditing, disabled = false, onEdit, onCancel, onChange }) => {
  const displayValue = value?.trim();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
        gap: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          textTransform: "capitalize",
          color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
        }}
      >
        Job alias
      </Typography>

      {isEditing ? (
        <TextField
          autoFocus
          size="small"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onBlur={onCancel}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onCancel();
            }

            if (event.key === "Escape") {
              event.preventDefault();
              onCancel();
            }
          }}
          sx={{
            flex: 1,
            maxWidth: 240,
            "& .MuiOutlinedInput-root": {
              height: 34,
              borderRadius: 1.5,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              fontSize: 14,
              "& fieldset": {
                borderColor: alphaVar(colors.orange.base, 0.25),
              },
            },
            "& .MuiInputBase-input": {
              fontSize: 13,
              fontWeight: 600,
              py: 0.5,
              px: 1,
              textAlign: "right",
            },
          }}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
              textAlign: "right",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayValue || "Unnamed Job"}
          </Typography>

          {!disabled && (
            <Tooltip title="Edit job alias">
              <Box
                onClick={onEdit}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  cursor: "pointer",
                  color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                  "&:hover": {
                    bgcolor: alphaVar(colors.orange?.base || "#f97316", 0.1),
                    color: colors.orange?.base || "#f97316",
                  },
                }}
              >
                <Edit sx={{ fontSize: 15 }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

const FrequencyOverviewRow = ({ value, isEditing, disabled = false, onEdit, onCancel, onChange }) => {
  const displayLabel = getJobFrequencyLabel(value);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
        gap: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          textTransform: "capitalize",
          color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
        }}
      >
        Frequency
      </Typography>

      {isEditing ? (
        <Select
          autoFocus
          size="small"
          value={value || EFrequencyType.WEEKLY}
          onChange={(event) => {
            onChange(event.target.value);
            onCancel();
          }}
          onClose={onCancel}
          MenuProps={menuPropsModal}
          sx={{
            minWidth: 160,
            fontSize: 13,
            fontWeight: 600,
            height: 32,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          }}
        >
          {JOB_FREQUENCY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13, fontWeight: 500 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
              textAlign: "right",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayLabel}
          </Typography>

          {!disabled && (
            <Tooltip title="Edit frequency">
              <Box
                onClick={onEdit}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  cursor: "pointer",
                  color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                  "&:hover": {
                    bgcolor: alphaVar(colors.orange?.base || "#f97316", 0.1),
                    color: colors.orange?.base || "#f97316",
                  },
                }}
              >
                <Edit sx={{ fontSize: 15 }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

const ObjectGrid = ({ items, fallbackAlt, fallbackName }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(4, 1fr)",
        sm: "repeat(3, 1fr)",
        md: "repeat(5, 1fr)",
      },
      gap: 1.25,
    }}
  >
    {items.map((item) => (
      <Box
        key={item?.uid}
        sx={{
          height: 120,
          borderRadius: 2,
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          border: `1px solid ${alphaVar(colors.orange.base, 0.12)}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 58,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
          }}
        >
          {item?.imageUrl ? (
            <Box
              component="img"
              src={item.imageUrl}
              alt={item?.name || fallbackAlt}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 800,
                color: colors.orange.base,
              }}
            >
              {String(item?.name || "?").charAt(0)}
            </Typography>
          )}
        </Box>

        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: colors.gray[700],
            textAlign: "center",
            lineHeight: 1.25,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item?.name || fallbackName}
        </Typography>
      </Box>
    ))}
  </Box>
);

const DescriptionSection = ({ descriptionJob, isEditingDescription, setIsEditingDescription, onChangeDescription }) => (
  <Box
    sx={{
      pt: 1.25,
      borderTop: `1px solid ${colors.gray?.[100] || "var(--fx-surface-border, #E2E8F0)"}`,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
        }}
      >
        Description
      </Typography>

      {!isEditingDescription && (
        <Tooltip title={descriptionJob.trim() ? "Edit description" : "Add description"}>
          <IconButton
            onClick={() => setIsEditingDescription(true)}
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              bgcolor: alphaVar(colors.orange.base, 0.08),
              color: colors.orange.base,
              flexShrink: 0,
              "&:hover": {
                bgcolor: alphaVar(colors.orange.base, 0.14),
              },
            }}
          >
            {descriptionJob.trim() ? <Edit sx={{ fontSize: 15 }} /> : <AddCircleOutline sx={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>
      )}
    </Box>

    {isEditingDescription ? (
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={3}
        maxRows={7}
        size="small"
        placeholder="Write a short description for this job"
        value={descriptionJob}
        onChange={(event) => {
          onChangeDescription(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            setIsEditingDescription(false);
          }

          if (event.key === "Escape") {
            event.preventDefault();
            setIsEditingDescription(false);
          }
        }}
        onBlur={() => {
          setIsEditingDescription(false);
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            alignItems: "flex-start",
            borderRadius: 2,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            fontSize: 14,
            boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
            "& fieldset": {
              borderColor: "rgba(249, 115, 22, 0.25)",
            },
            "&:hover fieldset": {
              borderColor: colors.orange[400],
            },
            "&.Mui-focused fieldset": {
              borderColor: colors.orange.base,
              borderWidth: 1.5,
            },
          },
          "& textarea": {
            resize: "vertical",
          },
        }}
      />
    ) : descriptionJob.trim() ? (
      <Typography
        sx={{
          fontSize: 13,
          color: colors.gray[700],
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {descriptionJob}
      </Typography>
    ) : (
      <Typography
        sx={{
          fontSize: 13,
          color: colors.gray[400],
          fontStyle: "italic",
        }}
      >
        No description for this job.
      </Typography>
    )}
  </Box>
);
